import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  CheckCircle, 
  CreditCard, 
  AlertCircle,
  Clock,
  DollarSign,
  Mail,
  Phone,
  MapPin,
  FileText,
  Lock,
  Printer
} from 'lucide-react';
import { toast } from "sonner";
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InvoicePayment = () => {
  const { paymentCode } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchInvoice();
  }, [paymentCode]);

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/invoices/payment-link/${paymentCode}`);
      setInvoice(response.data);
    } catch (error) {
      console.error('Error fetching invoice:', error);
      setError('No se pudo cargar la factura. El enlace puede ser inválido o haber expirado.');
    } finally {
      setLoading(false);
    }
  };

  const initiatePayment = async () => {
    try {
      setPaymentLoading(true);
      
      const paymentInfo = {
        origin_url: window.location.origin
      };

      const response = await axios.post(
        `${API}/invoices/payment-link/${paymentCode}/pay`,
        paymentInfo
      );
      
      if (response.data.url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.url;
      } else {
        throw new Error('No se recibió la URL de pago');
      }
      
    } catch (error) {
      console.error('Error initiating payment:', error);
      toast.error('Error al procesar el pago. Por favor intenta de nuevo.');
      setPaymentLoading(false);
    }
  };

  const formatCurrency = (amount, currency) => {
    const symbol = currency === 'EUR' ? '€' : '$';
    return `${symbol}${parseFloat(amount).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50 flex items-center justify-center p-4">
        <Card className="border-0 shadow-xl max-w-md w-full">
          <CardContent className="p-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-red-600 border-t-transparent mx-auto mb-4"></div>
              <p className="text-slate-600">Cargando factura...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50 flex items-center justify-center p-4">
        <Card className="border-0 shadow-xl max-w-md w-full">
          <CardContent className="p-8">
            <div className="text-center">
              <AlertCircle size={64} className="text-red-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                Error
              </h2>
              <p className="text-slate-600 mb-6">{error}</p>
              <Button 
                onClick={() => window.location.href = '/'}
                className="bg-red-600 hover:bg-red-700"
              >
                Volver al Inicio
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isPaid = invoice.status === 'paid';
  const invoiceData = invoice.invoice_data || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">
            Factura de Pago
          </h1>
          <p className="text-slate-600">
            DRYWALL DESING LLC
          </p>
        </div>

        {/* Main Invoice Card */}
        <Card className="border-0 shadow-2xl bg-white mb-6">
          <CardHeader className="bg-gradient-to-r from-slate-900 to-red-600 text-white">
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="text-2xl mb-2">
                  Factura #{invoice.invoice_number}
                </CardTitle>
                <p className="text-slate-200 text-sm">
                  Fecha: {formatDate(invoice.created_at)}
                </p>
              </div>
              <Badge 
                className={`px-4 py-2 text-lg ${
                  isPaid 
                    ? 'bg-green-500' 
                    : 'bg-yellow-500'
                }`}
              >
                {isPaid ? (
                  <>
                    <CheckCircle size={18} className="mr-2" />
                    PAGADO
                  </>
                ) : (
                  <>
                    <Clock size={18} className="mr-2" />
                    PENDIENTE
                  </>
                )}
              </Badge>
            </div>
          </CardHeader>

          <CardContent className="p-8">
            {/* Client Information */}
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="text-lg font-semibold text-red-600 mb-4 flex items-center gap-2">
                  <FileText size={20} />
                  Información del Cliente
                </h3>
                <div className="space-y-2 text-slate-700">
                  <p className="font-semibold text-lg">{invoice.client_name}</p>
                  {invoiceData.client_nif && (
                    <p className="text-sm"><strong>NIF/CIF:</strong> {invoiceData.client_nif}</p>
                  )}
                  {invoiceData.client_address && (
                    <div className="flex items-start gap-2">
                      <MapPin size={16} className="mt-1 flex-shrink-0" />
                      <span>{invoiceData.client_address}</span>
                    </div>
                  )}
                  {invoiceData.client_phone && (
                    <div className="flex items-center gap-2">
                      <Phone size={16} />
                      <span>{invoiceData.client_phone}</span>
                    </div>
                  )}
                  {invoiceData.client_email && (
                    <div className="flex items-center gap-2">
                      <Mail size={16} />
                      <span>{invoiceData.client_email}</span>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-red-600 mb-4">
                  Información de la Empresa
                </h3>
                <div className="space-y-2 text-slate-700">
                  <p className="font-semibold text-lg">DRYWALL DESING LLC</p>
                  <div className="flex items-start gap-2">
                    <MapPin size={16} className="mt-1 flex-shrink-0" />
                    <span>412 W 7th St, Clovis NM 88101</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone size={16} />
                    <span>+1 (505) 226-0487</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail size={16} />
                    <span>info@drywall-design.com</span>
                  </div>
                  <p className="text-sm">EIN: 30-1416729</p>
                </div>
              </div>
            </div>

            {/* Service Details */}
            <div className="bg-slate-50 rounded-lg p-6 mb-8">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">
                Detalles del Servicio
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="font-semibold text-slate-900">
                    {invoiceData.service_name || 'Servicio de Drywall'}
                  </p>
                  {invoiceData.service_description && (
                    <p className="text-slate-600 text-sm mt-1">
                      {invoiceData.service_description}
                    </p>
                  )}
                </div>
                
                <div className="flex justify-between items-center pt-4 border-t border-slate-200">
                  <span className="text-slate-600">Subtotal:</span>
                  <span className="font-semibold">
                    {formatCurrency(invoice.amount, invoice.currency)}
                  </span>
                </div>
                
                {invoiceData.tax_rate > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">
                      Impuesto ({(invoiceData.tax_rate * 100).toFixed(1)}%):
                    </span>
                    <span className="font-semibold">
                      {formatCurrency(invoice.amount * invoiceData.tax_rate, invoice.currency)}
                    </span>
                  </div>
                )}
                
                <div className="flex justify-between items-center pt-4 border-t-2 border-red-600">
                  <span className="text-xl font-bold text-slate-900">Total:</span>
                  <span className="text-2xl font-bold text-red-600">
                    {formatCurrency(
                      invoice.amount * (1 + (invoiceData.tax_rate || 0)), 
                      invoice.currency
                    )}
                  </span>
                </div>
              </div>
            </div>

            {/* Dates */}
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-slate-600 mb-1">Fecha de Emisión</p>
                <p className="font-semibold text-slate-900">
                  {formatDate(invoice.created_at)}
                </p>
              </div>
              {isPaid && invoice.paid_at && (
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm text-slate-600 mb-1">Fecha de Pago</p>
                  <p className="font-semibold text-slate-900">
                    {formatDate(invoice.paid_at)}
                  </p>
                </div>
              )}
            </div>

            {/* Payment Button or Status */}
            {isPaid ? (
              <div className="bg-green-50 border-2 border-green-500 rounded-lg p-6 text-center">
                <CheckCircle size={48} className="text-green-600 mx-auto mb-3" />
                <h3 className="text-xl font-bold text-green-900 mb-2">
                  ¡Factura Pagada!
                </h3>
                <p className="text-green-700 mb-4">
                  Esta factura ya ha sido pagada. Gracias por tu pago.
                </p>
                <Button 
                  onClick={() => window.print()}
                  className="bg-slate-600 hover:bg-slate-700 text-white"
                >
                  <Printer size={18} className="mr-2" />
                  Imprimir / Guardar PDF
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                  <Lock size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-blue-900 mb-1">Pago Seguro</p>
                    <p className="text-sm text-blue-700">
                      Tu pago está protegido con encriptación de nivel bancario a través de Stripe.
                    </p>
                  </div>
                </div>
                
                <Button 
                  onClick={initiatePayment}
                  disabled={paymentLoading}
                  className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                  size="lg"
                >
                  {paymentLoading ? (
                    <div className="flex items-center gap-3">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                      Redirigiendo a pago seguro...
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <CreditCard size={24} />
                      Pagar Ahora - {formatCurrency(
                        invoice.amount * (1 + (invoiceData.tax_rate || 0)), 
                        invoice.currency
                      )}
                    </div>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Help Card */}
        <Card className="border-0 shadow-lg bg-white">
          <CardContent className="p-6 text-center">
            <h3 className="font-semibold text-slate-900 mb-2">
              ¿Necesitas Ayuda?
            </h3>
            <p className="text-slate-600 text-sm mb-4">
              Si tienes preguntas sobre esta factura, contáctanos directamente.
            </p>
            <div className="flex justify-center gap-4">
              <a 
                href="mailto:info@drywall-design.com" 
                className="text-red-600 hover:underline text-sm font-medium"
              >
                Enviar Email
              </a>
              <a 
                href="tel:+15052260487" 
                className="text-red-600 hover:underline text-sm font-medium"
              >
                Llamar Ahora
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default InvoicePayment;
